/**
 * GRG-59 — Sticky in-page tab nav for the PDP redesign.
 *
 * A self-contained web component that turns a row of in-page tabs into a
 * sticky nav with scroll-spy, smooth scroll and (desktop) a mirrored
 * price + add-to-cart cluster. Replaces the legacy `.product-sticky-bar`
 * behaviour baked into the auto-generated main.build.js, without touching it.
 *
 * Markup contract (see snippets/pd-sticky-tabs.liquid):
 *   <pd-product-sticky-tabs>
 *     <div role="tablist" data-tablist>
 *       <a role="tab" data-pd-tab data-target="section-anchor-id" href="#...">Label</a>
 *       ...
 *     </div>
 *     <div data-sticky-price></div>        (desktop only, optional)
 *     <button data-sticky-atc>...</button>  (desktop only, optional)
 *   </pd-product-sticky-tabs>
 *
 * Tabs whose `data-target` anchor is not present in the DOM are either hidden or
 * shown-but-disabled, per the `hide-unavailable` attribute (driven by the
 * section's "Hide tabs for missing sections" setting). Hiding is the default:
 * which sections a product has depends on its metafields, so sparse products
 * would otherwise carry several dead tabs (#191).
 */
class PdProductStickyTabs extends HTMLElement {
	connectedCallback() {
		this.tablist = this.querySelector('[data-tablist]');
		this.tabs = Array.from(this.querySelectorAll('[data-pd-tab]'));
		this.fade = this.querySelector('[data-tabs-fade]');
		this.tabsWrap = this.querySelector('[data-tabs-wrap]');
		// Section setting (see the section schema): hide tabs whose target section
		// isn't on this product, instead of showing them greyed out.
		this.hideUnavailable = this.hasAttribute('hide-unavailable');
		this.header =
			document.getElementById('shopify-section-header') ||
			document.querySelector('.section-header, .shopify-section-header, header');

		this.activeIndex = -1;
		this.headerHeight = 0;
		this.stickyTop = -1;
		// Cache the MediaQueryList once — matchMedia() allocates a new object on
		// every call, and prefersReducedMotion() runs on every tab/scroll action.
		this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

		this.filterTabs();
		if (!this.tabs.length) {
			this.hidden = true;
			return;
		}

		this.measureHeader();

		// Scroll-spy / keyboard / active state only apply to enabled tabs
		// (those whose target section is on the page).
		if (this.enabledTabs.length) {
			// First enabled tab is active until scroll-spy says otherwise.
			this.setActive(0);
			this.buildObserver();
			this.bindTabEvents();
		}

		this.bindActions();
		this.syncPrice();
		this.bindWishlist();

		this._onResize = this.rafThrottle(() => {
			this.measureHeader();
			this.updateStickyTop();
			this.updatePinned();
			this.buildObserver();
			this.updateFade();
		});
		this._onScroll = this.rafThrottle(() => {
			this.updateStickyTop();
			this.updatePinned();
		});
		this._onTablistScroll = this.rafThrottle(() => this.updateFade());
		window.addEventListener('resize', this._onResize, { passive: true });
		window.addEventListener('scroll', this._onScroll, { passive: true });
		this.tablist?.addEventListener('scroll', this._onTablistScroll, {
			passive: true,
		});
		this.updateStickyTop();
		this.updatePinned();
		this.updateFade();
	}

	disconnectedCallback() {
		window.removeEventListener('resize', this._onResize);
		window.removeEventListener('scroll', this._onScroll);
		if (this._onTablistScroll) {
			this.tablist?.removeEventListener('scroll', this._onTablistScroll);
		}
		if (this._onScrollEnd) {
			window.removeEventListener('scrollend', this._onScrollEnd);
			this._onScrollEnd = null;
		}
		clearTimeout(this._spyTimer);
		if (this._onVariantChange) {
			document.removeEventListener('variant:change', this._onVariantChange);
		}
		this._io?.disconnect();
		this._priceObserver?.disconnect();
		this._priceRootObserver?.disconnect();
		this._atcObserver?.disconnect();
		this._atcRootObserver?.disconnect();
	}

	/* ---------- tabs ---------- */

	filterTabs() {
		this.enabledTabs = [];
		this.tabs.forEach((tab) => {
			const id = tab.dataset.target;
			const target = id ? document.getElementById(id) : null;
			if (target) {
				tab._target = target;
				tab.hidden = false;
				tab.classList.remove('is-disabled');
				tab.removeAttribute('aria-disabled');
				this.enabledTabs.push(tab);
			} else if (this.hideUnavailable) {
				// Section not on the page → drop the tab entirely. Which sections a
				// product has depends on its metafields, so on sparse products the
				// greyed-out alternative leaves several dead tabs in the bar (#191).
				tab._target = null;
				tab.hidden = true;
				tab.classList.remove('is-active');
				tab.setAttribute('tabindex', '-1');
				tab.removeAttribute('href');
			} else {
				// Section not on the page → keep the tab visible but disable it.
				tab._target = null;
				tab.hidden = false;
				tab.classList.add('is-disabled');
				tab.classList.remove('is-active');
				tab.setAttribute('aria-disabled', 'true');
				tab.setAttribute('tabindex', '-1');
				tab.removeAttribute('href');
			}
		});
		// With every tab hidden the strip would be an empty row above the border.
		// Drop the whole tab area but keep the host, so the price/ATC cluster and
		// the mobile bottom bar (both children of it) survive.
		if (this.tabsWrap) {
			this.tabsWrap.hidden = this.hideUnavailable && !this.enabledTabs.length;
		}
		this.enabledTabs.forEach((tab, i) => {
			tab.setAttribute('tabindex', i === 0 ? '0' : '-1');
			tab.setAttribute('aria-selected', 'false');
		});
	}

	setActive(index) {
		if (index === this.activeIndex || index < 0) return;
		this.enabledTabs.forEach((tab, i) => {
			const isActive = i === index;
			tab.classList.toggle('is-active', isActive);
			tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
		});
		this.activeIndex = index;
		this.scrollTabIntoView(index);
	}

	// Keep the active tab visible inside the mobile horizontal carousel.
	scrollTabIntoView(index) {
		const tab = this.enabledTabs[index];
		if (!tab || !this.tablist) return;
		if (this.tablist.scrollWidth <= this.tablist.clientWidth) return;
		this.tablist.scrollTo({
			left: Math.max(0, tab.offsetLeft - 20),
			behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
		});
	}

	/* ---------- mobile carousel fade ---------- */

	// The right-edge fade signals "more tabs this way", so it has no business
	// showing once there is nothing left to scroll to (or nothing to scroll at
	// all).
	updateFade() {
		if (!this.fade || !this.tablist) return;
		const { scrollLeft, clientWidth, scrollWidth } = this.tablist;
		// Sub-pixel widths mean scrollLeft + clientWidth never exactly equals
		// scrollWidth at the end — allow a pixel of slack.
		const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
		const noOverflow = scrollWidth <= clientWidth;
		this.fade.classList.toggle('is-hidden', atEnd || noOverflow);
	}

	bindTabEvents() {
		this.enabledTabs.forEach((tab, i) => {
			tab.addEventListener('click', (e) => {
				e.preventDefault();
				this.activateTab(i);
			});
			tab.addEventListener('keydown', (e) => this.onKeydown(e, i));
		});
	}

	activateTab(index) {
		this.setActive(index);
		this.scrollToTarget(this.enabledTabs[index]._target);
		this.focusTab(index);
	}

	// Manual-activation tablist: arrows move focus only; Enter/Space activates.
	onKeydown(e, i) {
		const len = this.enabledTabs.length;
		let next = i;
		switch (e.key) {
			case 'ArrowRight':
			case 'ArrowDown':
				next = (i + 1) % len;
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
				next = (i - 1 + len) % len;
				break;
			case 'Home':
				next = 0;
				break;
			case 'End':
				next = len - 1;
				break;
			case 'Enter':
			case ' ':
				e.preventDefault();
				this.activateTab(i);
				return;
			default:
				return;
		}
		e.preventDefault();
		this.focusTab(next);
	}

	focusTab(index) {
		this.enabledTabs.forEach((tab, i) => {
			tab.setAttribute('tabindex', i === index ? '0' : '-1');
		});
		this.enabledTabs[index].focus();
	}

	/* ---------- sticky offset ---------- */

	measureHeader() {
		this.headerHeight = this.header ? this.header.offsetHeight || 0 : 0;
	}

	// Sit just below the live header while it is visible; rise to the very top
	// once the header has scrolled away or hidden (theme hides header on scroll).
	updateStickyTop() {
		let top = 0;
		if (this.header) {
			top = Math.max(0, Math.round(this.header.getBoundingClientRect().bottom));
		}
		if (top !== this.stickyTop) {
			this.stickyTop = top;
			// Set on :root so the sticky section wrapper (an ancestor of this
			// element) can read it — a property set on `this` would not inherit up.
			document.documentElement.style.setProperty('--pd-tabs-top', `${top}px`);
		}
	}

	// Where the bar's bottom edge sits once it is pinned — i.e. the y coordinate a
	// section's top must land on. `headerHeight` alone is wrong: the theme's header
	// is not sticky, so it scrolls away and the bar rises to the top of the
	// viewport. `stickyTop` tracks the header's live bottom (0 once it has gone),
	// and is what the sticky `top` is actually set to.
	barOffset() {
		const top = this.stickyTop >= 0 ? this.stickyTop : this.headerHeight;
		return top + this.offsetHeight;
	}

	// Pinned = the bar has reached its sticky offset and is stuck to the top.
	// The price/ATC cluster is only shown in this state.
	updatePinned() {
		const offset = this.stickyTop >= 0 ? this.stickyTop : 0;
		const pinned = this.getBoundingClientRect().top <= offset + 1;
		if (pinned !== this._pinned) {
			this._pinned = pinned;
			this.classList.toggle('is-pinned', pinned);
			// Pinning changes where the bar's bottom edge actually is, which is the
			// line the scroll-spy measures against. Rebuilding on the flip (a
			// boolean, so at most a couple of times) keeps the line aligned without
			// the per-pixel churn of rebuilding on every scroll frame.
			this.buildObserver();
		}
	}

	/* ---------- scroll-spy ---------- */

	// Active section = the one crossing a thin line just below the sticky bar.
	buildObserver() {
		if (!this.enabledTabs.length) return;
		const offset = this.barOffset();
		const top = Math.round(offset);
		const bottom = Math.max(0, Math.round(window.innerHeight - offset - 1));
		// Skip the rebuild when the scroll-spy geometry hasn't actually changed —
		// width-only resizes (header/bar height and viewport height unchanged)
		// leave rootMargin identical, so disconnecting + reallocating is wasted
		// work (and a forced layout read of offsetHeight).
		const signature = `${top}/${bottom}`;
		if (signature === this._observerSignature) return;
		this._observerSignature = signature;
		this._io?.disconnect();
		// Reset on every rebuild: the fresh observer only emits events for targets
		// whose intersection *changes*, so anything already intersecting before the
		// resize would never re-fire and would linger as a stale entry.
		this._visible = new Set();
		this._io = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) this._visible.add(entry.target);
					else this._visible.delete(entry.target);
				});
				this.updateActiveFromVisible();
			},
			{ rootMargin: `-${top}px 0px -${bottom}px 0px`, threshold: 0 }
		);
		this.enabledTabs.forEach((tab) => this._io.observe(tab._target));
	}

	updateActiveFromVisible() {
		// A tab click owns the active state until its scroll settles — otherwise the
		// sections crossing the line *during* the animation would each steal it, and
		// the last one to fire (not the one clicked) would end up active.
		if (this._spyLocked) return;
		let bestIndex = -1;
		let bestTop = -Infinity;
		this.enabledTabs.forEach((tab, i) => {
			if (!this._visible.has(tab._target)) return;
			// Where two sections both touch the line (their shared boundary sits on
			// it), the active one is the section being entered — the *lower* of the
			// two, i.e. the greatest top. Picking the smallest top instead leaves the
			// outgoing section active, which is what made a Reviews click read as
			// "Image gallery".
			const top = tab._target.getBoundingClientRect().top;
			if (top >= bestTop) {
				bestTop = top;
				bestIndex = i;
			}
		});
		// Nothing on the line (in a gap) → keep the current active tab.
		if (bestIndex !== -1) this.setActive(bestIndex);
	}

	scrollToTarget(target) {
		if (!target) return;
		this.lockSpy();
		this._pendingTarget = target;
		this._corrections = 0;
		this.scrollTargetIntoPlace(
			target,
			this.prefersReducedMotion() ? 'auto' : 'smooth'
		);
	}

	scrollTargetIntoPlace(target, behavior) {
		const y =
			target.getBoundingClientRect().top + window.scrollY - this.barOffset();
		window.scrollTo({ top: Math.max(0, y), behavior });
	}

	// Hold the active tab (and the mobile tab-strip position) steady for the
	// duration of the programmatic scroll, then reconcile.
	lockSpy() {
		this._spyLocked = true;
		clearTimeout(this._spyTimer);
		if (this._onScrollEnd) {
			window.removeEventListener('scrollend', this._onScrollEnd);
			this._onScrollEnd = null;
		}
		const settle = () => {
			clearTimeout(this._spyTimer);
			if (this._onScrollEnd) {
				window.removeEventListener('scrollend', this._onScrollEnd);
				this._onScrollEnd = null;
			}
			this.correctLanding();
			// Release on the next frame, so the observer callback that follows the
			// corrective scroll evaluates the final position rather than a
			// mid-correction one.
			window.requestAnimationFrame(() => {
				this._spyLocked = false;
			});
		};
		// `scrollend` is the accurate signal; the timeout is both the fallback for
		// browsers without it and the safety net if the scroll never completes.
		if ('onscrollend' in window) {
			this._onScrollEnd = settle;
			window.addEventListener('scrollend', settle, { once: true });
			this._spyTimer = setTimeout(settle, 1200);
		} else {
			this._spyTimer = setTimeout(settle, 700);
		}
	}

	// The offset a click is aimed at is measured *before* the scroll, but the
	// theme's header is not sticky — it scrolls away mid-flight, so the bar ends
	// up higher than predicted and the section lands too low, leaving a slice of
	// the previous section on screen. Re-measure once the scroll has settled and
	// close the gap.
	correctLanding() {
		const target = this._pendingTarget;
		if (!target) return;
		this._pendingTarget = null;
		// The header may have scrolled out during the animation; `stickyTop` is only
		// refreshed by the throttled scroll handler, so re-read it before measuring.
		this.updateStickyTop();
		// `behavior: 'auto'` applies synchronously, so re-measure in place. Bounded
		// because each pass can shift the header/bar geometry again; two is ample
		// and can't spin.
		while (this._corrections < 2) {
			const delta = target.getBoundingClientRect().top - this.barOffset();
			if (Math.abs(delta) <= 2) return;
			this._corrections += 1;
			this.scrollTargetIntoPlace(target, 'auto');
		}
	}

	/* ---------- price + add to cart ---------- */

	// Mirror the main product price node so variant changes flow through
	// without coupling to the legacy product JS. The template renders two
	// product sections (legacy `main` + redesign `pd_main`); scope to the
	// section that owns the visible add-to-cart button. Mirrors into every
	// [data-sticky-price] target (desktop pinned cluster + mobile bottom bar).
	syncPrice() {
		this.priceTargets = Array.from(this.querySelectorAll('[data-sticky-price]'));
		if (!this.priceTargets.length) return;
		const source = this.resolvePriceSource();
		if (!source) return;

		this._priceSource = source;
		const copy = () => {
			for (const t of this.priceTargets) t.innerHTML = source.innerHTML;
		};
		copy();

		this._priceObserver?.disconnect();
		this._priceObserver = new MutationObserver(copy);
		this._priceObserver.observe(source, {
			childList: true,
			subtree: true,
			characterData: true,
		});

		this.watchPriceSourceSwap(source);
	}

	// The price node that owns the visible add-to-cart button.
	resolvePriceSource() {
		const scope = this.mainAtc?.closest('.shopify-section') || document;
		const candidates = Array.from(
			document.querySelectorAll('[id^="price-"][role="status"]')
		).filter((el) => !this.contains(el));
		return (
			candidates.find((el) => scope !== document && scope.contains(el)) ||
			candidates.find((el) => el.offsetParent !== null) ||
			candidates[0]
		);
	}

	// GRG-55 (#234) — the observer above is bound to one specific node, but the
	// bundle configurator's revertCombined() rebuilds the product info column
	// from its captured child list, which REPLACES the price element. The mirror
	// was then observing a detached node and froze on the swapped combined-product
	// price while the page showed the reverted one — two prices on screen, the
	// wrong one on the buy bar.
	//
	// assets/pd-bundle-options.js already re-acquires its own priceEl by id after
	// a rebuild (both in the swap path and in revertCombined); this does the same.
	// The section wrapper survives the rebuild, so watching that — rather than the
	// price node — is what still fires once the node underneath is gone. Only
	// childList/subtree is needed: this reacts to replacement, while the observer
	// above handles in-place text updates.
	watchPriceSourceSwap(source) {
		const root = source.closest('.shopify-section');
		if (!root) return;
		this._priceRootObserver?.disconnect();
		this._priceRootObserver = new MutationObserver(() => {
			if (this._priceSource?.isConnected) return;
			this.syncPrice();
		});
		this._priceRootObserver.observe(root, { childList: true, subtree: true });
	}

	// Proxy every [data-sticky-atc] button (desktop pinned + mobile bottom bar)
	// to the real product-form buy button, and mirror its disabled state and its
	// label ("Add to cart" / "Place order" / "Pre-order" / "Out of Stock" …).
	bindActions() {
		this.atcBtns = Array.from(this.querySelectorAll('[data-sticky-atc]'));
		if (!this.atcBtns.length) return;
		this.mainAtc = this.resolveMainAtc();
		for (const btn of this.atcBtns) {
			btn.addEventListener('click', (e) => {
				e.preventDefault();
				// The bundle swap rebuilds the product form (see watchAtcSwap), so a
				// cached button can be detached by the time the shopper clicks.
				if (!this.mainAtc?.isConnected) this.bindAtcMirror();
				this.mainAtc?.click();
			});
		}
		this.bindAtcMirror();
	}

	// Prefer the visible buy button (template has legacy + redesign forms).
	resolveMainAtc() {
		const atcButtons = Array.from(
			document.querySelectorAll('[data-add-to-card-btn]')
		);
		return (
			atcButtons.find((b) => b.offsetParent !== null) || atcButtons[0] || null
		);
	}

	// GRG-204 (#341) — the sticky buttons used to hard-code "Add to cart" and
	// only mirror `disabled`, so they disagreed with the main button whenever it
	// read anything else. Copy its label too, and re-copy whenever variant
	// change (global.js toggleAddButton) rewrites it.
	bindAtcMirror() {
		this._atcObserver?.disconnect();
		if (!this.mainAtc?.isConnected) this.mainAtc = this.resolveMainAtc();
		const main = this.mainAtc;
		if (!main) return;
		const sync = () => {
			const disabled =
				main.disabled || main.getAttribute('aria-disabled') === 'true';
			// The label span, not the button — the button also holds the spinner.
			const label = main
				.querySelector('.product-form__submit__text')
				?.textContent.trim();
			for (const btn of this.atcBtns) {
				btn.disabled = disabled;
				btn.classList.toggle('is-disabled', disabled);
				if (label && btn.textContent !== label) btn.textContent = label;
			}
		};
		sync();
		this._atcObserver = new MutationObserver(sync);
		this._atcObserver.observe(main, {
			attributes: true,
			attributeFilter: ['disabled', 'aria-disabled', 'class'],
			childList: true,
			subtree: true,
			characterData: true,
		});
		this.watchAtcSwap(main);
	}

	// Same failure as watchPriceSourceSwap (GRG-55 #234): the bundle combined
	// swap / revertCombined() replaces the product form, leaving the observer
	// above on a detached button. Watch the surviving section wrapper and rebind.
	watchAtcSwap(main) {
		const root = main.closest('.shopify-section');
		if (!root) return;
		this._atcRootObserver?.disconnect();
		this._atcRootObserver = new MutationObserver(() => {
			if (this.mainAtc?.isConnected) return;
			this.bindAtcMirror();
		});
		this._atcRootObserver.observe(root, { childList: true, subtree: true });
	}

	/* ---------- wishlist (Swym) ---------- */

	// Swym renders `data-variant-id` once from `product.variants[0]` at page load,
	// so without this it always saves the first variant. The variant picker
	// (VariantSelects in global.js) dispatches `variant:change` on `document` with
	// `detail.variant`; keep the attribute in step with the selected variant.
	bindWishlist() {
		this.wishlistBtn = this.querySelector('[data-swaction="addToWishlist"]');
		if (!this.wishlistBtn) return;
		this._onVariantChange = (e) => {
			const id = e.detail?.variant?.id;
			if (id) this.wishlistBtn.dataset.variantId = id;
		};
		document.addEventListener('variant:change', this._onVariantChange);
	}

	/* ---------- helpers ---------- */

	prefersReducedMotion() {
		return this._reducedMotion.matches;
	}

	rafThrottle(fn) {
		let ticking = false;
		return () => {
			if (ticking) return;
			ticking = true;
			window.requestAnimationFrame(() => {
				ticking = false;
				fn();
			});
		};
	}
}

if (!customElements.get('pd-product-sticky-tabs')) {
	customElements.define('pd-product-sticky-tabs', PdProductStickyTabs);
}
